import { getErrorMessage, isSafeRelativePath } from '@/lib/utils'
import {
  queryOptions,
  useMutation,
  useQueryClient,
  useSuspenseQuery,
  type UseSuspenseQueryOptions,
} from '@tanstack/react-query'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { toast } from 'sonner'
import { logoutActiveStripeConnectInstance } from '@/lib/stripe-connect-session'
import { organizationQueryKeys } from '../organizations/organizations.query'
import {
  forgotPassword,
  getAuthUser,
  login,
  logout,
  resetPassword,
  signUp,
  status,
  type AuthState,
  type LoginInput,
} from './auth.api'
import type { AuthUser } from './auth.types'

const SESSION_DURATION = 1000 * 60 * 60 * 2 // 2 hours

interface AuthQueryRoot {
  entity: 'auth'
}

type AuthQueryKey<Scope extends string> = [AuthQueryRoot & { scope: Scope }]

export const authQueryKeys: {
  all: [AuthQueryRoot]
  status: () => AuthQueryKey<'status'>
  user: () => AuthQueryKey<'user'>
} = {
  all: [{ entity: 'auth' }],
  status: () => [{ ...authQueryKeys.all[0], scope: 'status' }],
  user: () => [{ ...authQueryKeys.all[0], scope: 'user' }],
}

// The logout error is an axios error when the request reached the API; read its HTTP status.
function readResponseStatus(error: unknown): unknown {
  if (typeof error !== 'object' || error === null || !('response' in error)) {
    return undefined
  }

  const { response } = error
  if (typeof response !== 'object' || response === null || !('status' in response)) {
    return undefined
  }

  return response.status
}

export const authQueries = {
  status: () =>
    // This is just a convenience to redirect authenticated users from the login page to the dashboard.
    queryOptions({
      queryKey: authQueryKeys.status(),
      queryFn: status,
      staleTime: 500,
      gcTime: 1000,
      retry: false,
    }),
  user: () =>
    queryOptions({
      queryKey: authQueryKeys.user(),
      queryFn: getAuthUser,
      // Once the user is logged in, there is really no reason to keep refetching their info
      // all the time. So, we'll allow this to stay in the cache for a long(ish) time.
      staleTime: SESSION_DURATION / 2,
      gcTime: SESSION_DURATION,
      retry: false,
    }),
}

export function useAuthState(
  params: Partial<
    Omit<
      UseSuspenseQueryOptions<AuthState, Error, AuthState, AuthQueryKey<'user'>>,
      'queryKey' | 'queryFn'
    >
  > = {}
) {
  return useSuspenseQuery({
    ...authQueries.user(),
    ...params,
  })
}

export function useLoginMutation() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const search = useSearch({ strict: false })

  return useMutation({
    mutationFn: async (values: LoginInput) => {
      await login(values)

      const authState = await queryClient.fetchQuery({
        ...authQueries.user(),
        staleTime: 0,
      })

      if (!authState?.isAuthenticated || !authState?.user) {
        throw new Error('We could not confirm your session. Please try again.')
      }

      return authState.user
    },
    onSuccess: async (user: AuthUser) => {
      const { organization_id } = user
      queryClient.setQueryData<AuthState>(authQueryKeys.user(), { isAuthenticated: true, user })
      queryClient.setQueryData(organizationQueryKeys.show(organization_id), user.organization)
      toast.success('Login successful')
      // The login route's search schema keeps `next` a string; narrow it from the loose search type.
      const next = typeof search?.next === 'string' ? search.next : undefined
      const redirect = isSafeRelativePath(next) ? next : `/${organization_id}/admin`
      return navigate({ to: redirect })
    },
  })
}

export function useLogoutMutation() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const handleLogout = async () => {
    await logoutActiveStripeConnectInstance()
    await navigate({ to: '/login' })

    toast.success('Logout successful')
  }

  return useMutation({
    mutationFn: logout,
    onSuccess: handleLogout,
    onError: async (error) => {
      // This means we were already logged out
      const responseStatus = readResponseStatus(error)
      if (responseStatus === 401 || responseStatus === 419) {
        return await handleLogout()
      }

      console.error('logout error', error)
      queryClient.clear()
      toast.error('Logout failed', { description: getErrorMessage(error) })
    },
  })
}

export function useSignUpMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: signUp,
    onSettled: () =>
      queryClient.invalidateQueries({
        queryKey: authQueryKeys.user(),
        refetchType: 'all',
      }),
  })
}

// Submit a form with your email requesting a password reset link
export function useForgotPasswordMutation() {
  return useMutation({
    mutationFn: forgotPassword,
  })
}

// Submit a form with your new password
export function useResetPasswordMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: resetPassword,
    onSettled: () =>
      queryClient.invalidateQueries({
        queryKey: authQueryKeys.user(),
        refetchType: 'all',
      }),
  })
}
